import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { toast } from 'react-toastify';
import backupService, { BackupStatus, BackupInfo } from '../../services/backupService';
import { 
    Database, 
    Download, 
    Upload, 
    Trash2, 
    RefreshCw, 
    AlertCircle, 
    CheckCircle, 
    Clock,
    HardDrive,
    Cloud,
    Archive
} from 'lucide-react';

const BackupManagementPage: React.FC = () => {
    const currentUser = useSelector((state: RootState) => state.auth.user);
    const [status, setStatus] = useState<BackupStatus | null>(null);
    const [backups, setBackups] = useState<BackupInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [filterType, setFilterType] = useState<string>('all');
    const [retentionDays, setRetentionDays] = useState(30);

    // Check permissions
    const isOwner = currentUser?.role === 'owner';
    const isAdmin = currentUser?.role === 'admin';
    const canManageBackups = isOwner || isAdmin;
    const canRestore = isOwner; // Only owner can restore

    useEffect(() => {
        if (!canManageBackups) {
            toast.error('Anda tidak memiliki akses ke halaman ini');
            return;
        }
        loadData();
    }, [canManageBackups]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [statusData, backupsData] = await Promise.all([
                backupService.getStatus(),
                backupService.listBackups(filterType === 'all' ? undefined : filterType)
            ]);
            setStatus(statusData);
            setBackups(backupsData.backups);
        } catch (error: any) {
            toast.error('Gagal memuat data backup: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateBackup = async () => {
        if (!canManageBackups) return;
        
        setActionLoading('create');
        try {
            const result = await backupService.createBackupSync('manual');
            toast.success(result.message);
            await loadData();
        } catch (error: any) {
            toast.error('Gagal membuat backup: ' + error.message);
        } finally {
            setActionLoading(null);
        }
    };

    const handleRestoreBackup = async (gcsPath: string, filename: string) => {
        if (!canRestore) {
            toast.error('Hanya Owner yang dapat restore database');
            return;
        }

        const confirmed = window.confirm(
            `⚠️ PERINGATAN!\n\n` +
            `Restore database akan MENIMPA semua data yang ada!\n\n` +
            `File: ${filename}\n\n` +
            `Proses ini TIDAK DAPAT DIBATALKAN!\n\n` +
            `Pastikan aplikasi dalam maintenance mode.\n\n` +
            `Lanjutkan restore?`
        );

        if (!confirmed) return;

        // Double confirmation
        const doubleConfirm = window.confirm(
            `Konfirmasi kedua:\n\n` +
            `Apakah Anda YAKIN ingin restore database?\n\n` +
            `Ketik "RESTORE" di prompt berikutnya untuk melanjutkan.`
        );

        if (!doubleConfirm) return;

        const finalConfirm = window.prompt('Ketik "RESTORE" untuk melanjutkan:');
        if (finalConfirm !== 'RESTORE') {
            toast.info('Restore dibatalkan');
            return;
        }

        setActionLoading(`restore-${gcsPath}`);
        try {
            const result = await backupService.restoreBackup(gcsPath);
            toast.success(result.message);
            await loadData();
        } catch (error: any) {
            toast.error('Gagal restore backup: ' + error.message);
        } finally {
            setActionLoading(null);
        }
    };

    const handleDeleteBackup = async (gcsPath: string, filename: string) => {
        if (!canManageBackups) return;

        const confirmed = window.confirm(`Hapus backup?\n\n${filename}\n\nTidak dapat dibatalkan!`);
        if (!confirmed) return;

        setActionLoading(`delete-${gcsPath}`);
        try {
            const result = await backupService.deleteBackup(gcsPath);
            toast.success(result.message);
            await loadData();
        } catch (error: any) {
            toast.error('Gagal menghapus backup: ' + error.message);
        } finally {
            setActionLoading(null);
        }
    };

    const handleCleanupOld = async () => {
        if (!canManageBackups) return;

        const confirmed = window.confirm(
            `Hapus semua backup lebih dari ${retentionDays} hari?\n\n` +
            `Proses ini tidak dapat dibatalkan!`
        );
        if (!confirmed) return;

        setActionLoading('cleanup');
        try {
            const result = await backupService.cleanupOldBackupsSync(retentionDays);
            toast.success(result.message);
            await loadData();
        } catch (error: any) {
            toast.error('Gagal cleanup: ' + error.message);
        } finally {
            setActionLoading(null);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString('id-ID', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatSize = (sizeInBytes: number) => {
        const sizeMB = sizeInBytes / (1024 * 1024);
        if (sizeMB < 1024) {
            return `${sizeMB.toFixed(2)} MB`;
        }
        return `${(sizeMB / 1024).toFixed(2)} GB`;
    };

    if (!canManageBackups) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <AlertCircle className="inline mr-2" />
                    Anda tidak memiliki akses ke halaman ini
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <RefreshCw className="animate-spin mr-2" />
                Memuat data backup...
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="mb-6">
                <h1 className="text-3xl font-bold flex items-center gap-2">
                    <Database className="w-8 h-8" />
                    Backup Management
                </h1>
                <p className="text-gray-600 mt-2">
                    Kelola backup database ke Google Cloud Storage
                </p>
            </div>

            {/* Status Card */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white border rounded-lg p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Status Sistem</p>
                            <p className="text-lg font-bold">
                                {status?.configured ? (
                                    <span className="text-green-600 flex items-center gap-1">
                                        <CheckCircle className="w-5 h-5" /> Configured
                                    </span>
                                ) : (
                                    <span className="text-red-600 flex items-center gap-1">
                                        <AlertCircle className="w-5 h-5" /> Not Configured
                                    </span>
                                )}
                            </p>
                        </div>
                        <Cloud className={`w-12 h-12 ${status?.configured ? 'text-green-500' : 'text-gray-300'}`} />
                    </div>
                </div>

                <div className="bg-white border rounded-lg p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Total Backups</p>
                            <p className="text-2xl font-bold">{status?.total_backups || 0}</p>
                        </div>
                        <Archive className="w-12 h-12 text-blue-500" />
                    </div>
                </div>

                <div className="bg-white border rounded-lg p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Total Size</p>
                            <p className="text-2xl font-bold">{status?.total_size_mb.toFixed(2)} MB</p>
                        </div>
                        <HardDrive className="w-12 h-12 text-purple-500" />
                    </div>
                </div>
            </div>

            {/* Configuration Status */}
            {!status?.configured && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                    <h3 className="font-bold text-yellow-800 mb-2 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5" />
                        Setup Required
                    </h3>
                    <div className="text-sm text-yellow-700 space-y-2">
                        {!status?.gcloud_authenticated && (
                            <p>❌ GCloud CLI belum authenticated. Run: <code className="bg-yellow-100 px-2 py-1 rounded">gcloud auth login</code></p>
                        )}
                        {!status?.bucket_exists && (
                            <p>❌ Bucket belum dibuat. Run: <code className="bg-yellow-100 px-2 py-1 rounded">gcloud storage buckets create gs://{status?.bucket_name} --location=asia-southeast2</code></p>
                        )}
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="bg-white border rounded-lg p-4 mb-6">
                <div className="flex flex-wrap gap-3">
                    <button
                        onClick={handleCreateBackup}
                        disabled={!status?.configured || actionLoading === 'create'}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                    >
                        {actionLoading === 'create' ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                            <Upload className="w-4 h-4" />
                        )}
                        Create Backup Now
                    </button>

                    <button
                        onClick={loadData}
                        disabled={!!actionLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                    >
                        <RefreshCw className={`w-4 h-4 ${actionLoading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>

                    <div className="flex items-center gap-2 ml-auto">
                        <label className="text-sm text-gray-600">Retention:</label>
                        <input
                            type="number"
                            value={retentionDays}
                            onChange={(e) => setRetentionDays(parseInt(e.target.value) || 30)}
                            className="w-20 px-2 py-1 border rounded"
                            min="1"
                        />
                        <span className="text-sm text-gray-600">days</span>
                        <button
                            onClick={handleCleanupOld}
                            disabled={!status?.configured || actionLoading === 'cleanup'}
                            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                        >
                            {actionLoading === 'cleanup' ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                                <Trash2 className="w-4 h-4" />
                            )}
                            Cleanup Old
                        </button>
                    </div>
                </div>
            </div>

            {/* Filter */}
            <div className="bg-white border rounded-lg p-4 mb-6">
                <div className="flex items-center gap-4">
                    <span className="text-sm font-semibold text-gray-700">Filter:</span>
                    <button
                        onClick={() => { setFilterType('all'); loadData(); }}
                        className={`px-3 py-1 rounded ${filterType === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        All
                    </button>
                    <button
                        onClick={() => { setFilterType('manual'); loadData(); }}
                        className={`px-3 py-1 rounded ${filterType === 'manual' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        Manual
                    </button>
                    <button
                        onClick={() => { setFilterType('scheduled'); loadData(); }}
                        className={`px-3 py-1 rounded ${filterType === 'scheduled' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                    >
                        Scheduled
                    </button>
                </div>
            </div>

            {/* Backups List */}
            <div className="bg-white border rounded-lg shadow-sm">
                <div className="p-4 border-b">
                    <h2 className="text-xl font-bold">Available Backups ({backups.length})</h2>
                </div>

                {backups.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        <Archive className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>Belum ada backup</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Filename</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Type</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Created</th>
                                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Size</th>
                                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {backups.map((backup) => (
                                    <tr key={backup.gcs_path} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm font-mono">{backup.filename}</td>
                                        <td className="px-4 py-3 text-sm">
                                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                                backup.type === 'manual' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                                            }`}>
                                                {backup.type}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm flex items-center gap-1">
                                            <Clock className="w-4 h-4 text-gray-400" />
                                            {formatDate(backup.created_at)}
                                        </td>
                                        <td className="px-4 py-3 text-sm">{formatSize(backup.size)}</td>
                                        <td className="px-4 py-3 text-sm text-right">
                                            <div className="flex justify-end gap-2">
                                                {canRestore && (
                                                    <button
                                                        onClick={() => handleRestoreBackup(backup.gcs_path, backup.filename)}
                                                        disabled={!!actionLoading}
                                                        className="p-2 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                                                        title="Restore Database"
                                                    >
                                                        {actionLoading === `restore-${backup.gcs_path}` ? (
                                                            <RefreshCw className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <Download className="w-4 h-4" />
                                                        )}
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleDeleteBackup(backup.gcs_path, backup.filename)}
                                                    disabled={!!actionLoading}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                                                    title="Delete Backup"
                                                >
                                                    {actionLoading === `delete-${backup.gcs_path}` ? (
                                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <Trash2 className="w-4 h-4" />
                                                    )}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Last Backup Info */}
            {status?.last_backup && (
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        Last Backup
                    </h3>
                    <div className="text-sm text-blue-700">
                        <p>Filename: <span className="font-mono">{status.last_backup.filename}</span></p>
                        <p>Created: {formatDate(status.last_backup.created_at)}</p>
                        <p>Size: {formatSize(status.last_backup.size)}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BackupManagementPage;
